(() => {
  'use strict';
  if (window.TDRampaEditorial) { window.TDRampaEditorial.init(); return; }
  const instances = new WeakMap();
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  function initialize(root) {
    if (!root || instances.has(root)) return;
    const dataElement = root.querySelector('#td-rampa-product-data');
    const form = root.querySelector('#td-rampa-purchase');
    if (!dataElement || !form) return;
    let product;
    try { product = JSON.parse(dataElement.textContent); } catch { return; }
    if (!Array.isArray(product.variants) || !product.variants.length) return;
    const variants = product.variants.map(variant => ({ ...variant, id: String(variant.id) }));
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const abort = new AbortController();
    const observers = [];
    const listen = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: abort.signal });
    const observe = (observer, target, options) => { if (target) { observer.observe(target, options); observers.push(observer); } return observer; };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const defaultVariant = variants.find(variant => variant.id === String(product.selectedVariantId)) || variants.find(variant => variant.available) || variants[0];
    let variant = variants.find(item => item.id === new URLSearchParams(location.search).get('variant')) || defaultVariant;
    let current = 0;
    let quantity = 1;
    let pending = false;
    let suppressZoom = false;
    let pointerStart = null;
    let galleryToken = 0;
    let playbackToken = 0;
    let stickyFrame = 0;
    let destroyed = false;
    const openers = new WeakMap();
    const galleryVideo = $('#product-video');
    const allVideos = $$('video');
    const ugcVideos = $$('.ugc-video');
    const ratios = new Map(ugcVideos.map(video => [video, 0]));
    const attempted = new WeakSet();
    const defaultShippingEstimate = $('#shipping-estimate')?.textContent || '';
    const money = value => new Intl.NumberFormat('es-CL', { style: 'currency', currency: product.currency || 'CLP', maximumFractionDigits: product.currency && product.currency !== 'CLP' ? 2 : 0 }).format(Number(value || 0) / Number(product.moneyDivisor || 1));
    const localeRoot = () => window.Shopify?.routes?.root || '/';
    const cartURL = () => window.routes?.cart_url || localeRoot() + 'cart';
    const hasOverlay = () => Boolean(root.querySelector('dialog[open]') || document.querySelector('cart-drawer.active,cart-notification.active,details.menu-drawer-container[open]'));
    const blocked = () => destroyed || document.hidden || hasOverlay();
    const saveData = () => Boolean(navigator.connection?.saveData);
    const t = (key, fallback, values = {}) => String(product.i18n?.[key] || fallback).replace(/\{\{?\s*(\w+)\s*\}?\}/g, (match, name) => values[name] ?? match);
    const stepLabel = () => variant.dimensions.steps + ' ' + t('steps', 'escalones');
    const maxQuantity = () => Math.max(1, Math.min(10, Number(variant.maxQuantity) || 10));
    const safeQuantity = value => Number.isFinite(Number(value)) ? Math.max(1, Math.min(maxQuantity(), Math.floor(Number(value)))) : 1;
    const text = (selector, value) => { const element = $(selector); if (element) element.textContent = value; };
    const setHidden = (selector, value) => { const element = $(selector); if (element) element.hidden = value; };
    const event = (name, detail = {}) => document.dispatchEvent(new CustomEvent('td:product-form:submit-' + name, { detail: { formId: form.id, ...detail } }));
    const pauseAll = except => allVideos.forEach(video => { if (video !== except) video.pause(); });

    function updateSticky() {
      const add = $('#add-to-cart');
      const sticky = $('#sticky-buy');
      if (!add || !sticky) return;
      const bounds = add.getBoundingClientRect();
      const hidden = bounds.bottom > 0 || hasOverlay();
      sticky.dataset.hidden = String(hidden);
      sticky.inert = hidden;
      sticky.setAttribute('aria-hidden', String(hidden));
    }
    function scheduleSticky() {
      if (!stickyFrame) stickyFrame = requestAnimationFrame(() => { stickyFrame = 0; updateSticky(); });
    }
    function openDialog(id, opener) {
      const dialog = $(id);
      if (!dialog || dialog.open) return;
      if (typeof dialog.showModal !== 'function') { dialog.setAttribute('open', ''); dialog.scrollIntoView({ block: 'center' }); return; }
      openers.set(dialog, opener || document.activeElement);
      playbackToken++;
      galleryToken++;
      pauseAll();
      dialog.showModal();
      updateSticky();
    }
    $$('dialog').forEach(dialog => {
      listen(dialog, 'close', () => { const opener = openers.get(dialog); if (opener?.isConnected) opener.focus({ preventScroll: true }); updateSticky(); });
      listen(dialog, 'click', click => {
        if (click.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (click.clientX < bounds.left || click.clientX > bounds.right || click.clientY < bounds.top || click.clientY > bounds.bottom) dialog.close();
      });
    });
    const media = () => variant.gallery?.[current];
    const legacyBase = () => (product.assetBase || 'assets/') + 'rampa-' + variant.dimensions.steps;
    const mediaSource = item => item?.src || (item?.image ? item.image + '.webp' : '');
    const videoPoster = item => item?.poster || legacyBase() + '-poster-540.webp';
    const videoSource = item => (matchMedia('(max-width:760px)').matches && item?.srcMobile) || item?.src || legacyBase() + '-demo-' + (matchMedia('(max-width:760px)').matches ? '480' : '720') + '.mp4';
    function stopGallery() {
      galleryToken++;
      setHidden('#video-fallback', true);
      galleryVideo?.pause();
      $('#gallery-stage')?.classList.remove('playing');
      setHidden('#video-play', false);
    }
    function renderGallery() {
      const item = media();
      if (!item) return;
      const thumbs = $('#gallery-thumbs');
      const thumbHadFocus = thumbs?.contains(document.activeElement);
      stopGallery();
      const isVideo = item.type === 'video';
      setHidden('#image-open', isVideo);
      setHidden('#video-frame', !isVideo);
      setHidden('#zoom-button', isVideo);
      text('#gallery-label', stepLabel() + ' · ' + variant.use);
      text('#gallery-caption', item.caption + ' · ' + stepLabel());
      text('#gallery-position', current + 1 + ' / ' + variant.gallery.length);
      if (isVideo && galleryVideo) {
        galleryVideo.poster = videoPoster(item);
        galleryVideo.setAttribute('aria-label', item.alt || t('demo_label', 'Demostración de la rampa de {{steps}} escalones', { steps: variant.dimensions.steps }));
        if (galleryVideo.dataset.mediaKey !== variant.id + ':' + current) {
          galleryVideo.removeAttribute('src');
          galleryVideo.dataset.mediaKey = variant.id + ':' + current;
          galleryVideo.load();
        }
      } else {
        const image = $('#main-image');
        if (image) {
          image.src = mediaSource(item);
          if (item.srcset) image.srcset = item.srcset;
          else if (item.image) image.srcset = item.image + '-540.webp 540w, ' + item.image + '.webp 1000w';
          else image.removeAttribute('srcset');
          image.alt = item.alt || item.caption;
        }
      }
      if (thumbs) {
        thumbs.innerHTML = variant.gallery.map((entry, index) => {
          const thumbnail = entry.thumb || (entry.type === 'video' ? videoPoster(entry) : entry.image ? entry.image + '-180.webp' : mediaSource(entry));
          return '<button type="button" data-media="' + index + '" aria-label="' + escapeHTML(entry.type === 'video' ? t('view_video', 'Ver video') : t('view_image', 'Ver imagen: {{caption}}', { caption: entry.caption })) + '" aria-current="' + (index === current) + '"><img src="' + escapeHTML(thumbnail) + '" width="72" height="72" loading="lazy" decoding="async" alt="">' + (entry.type === 'video' ? '<span aria-hidden="true">▷</span>' : '') + '</button>';
        }).join('');
        if (thumbHadFocus) thumbs.querySelector('[aria-current="true"]')?.focus({ preventScroll: true });
      }
    }
    function clearError() { ['#purchase-error', '#sticky-error'].forEach(selector => { const element = $(selector); if (element) { element.hidden = true; element.replaceChildren(); } }); }
    function showError(message, reviewCart = false) {
      ['#purchase-error', '#sticky-error'].forEach(selector => {
        const element = $(selector);
        if (!element) return;
        element.textContent = message;
        if (reviewCart) {
          const link = document.createElement('a');
          link.href = cartURL();
          link.textContent = ' ' + t('cart_review', 'Revisar mi carrito');
          element.append(link);
        }
        element.hidden = false;
      });
    }
    function syncPurchase() {
      quantity = safeQuantity(quantity);
      const quantityInput = $('#quantity');
      if (quantityInput) { quantityInput.value = quantity; quantityInput.max = maxQuantity(); quantityInput.readOnly = pending; }
      if ($('#quantity-minus')) $('#quantity-minus').disabled = pending || quantity <= 1;
      if ($('#quantity-plus')) $('#quantity-plus').disabled = pending || quantity >= maxQuantity();
      $$('[name="id"][type="radio"]').forEach(input => { input.checked = input.value === variant.id; input.disabled = !variants.find(item => item.id === input.value)?.available || (pending && input.value !== variant.id); });
      text('#selected-summary', stepLabel() + ' · ' + quantity + ' ' + (quantity === 1 ? t('unit', 'unidad') : t('units', 'unidades')));
      text('#sticky-price', money(variant.price * quantity));
      text('#sticky-variant', stepLabel() + ' · ' + variant.dimensions.heightCm + ' ' + t('height', 'cm alto') + (quantity > 1 ? ' · ×' + quantity : ''));
      ['#add-to-cart', '#sticky-add'].forEach(selector => {
        const button = $(selector);
        if (!button) return;
        button.disabled = pending || !variant.available;
        button.setAttribute('aria-busy', String(pending));
        button.setAttribute('aria-disabled', String(button.disabled));
        button.innerHTML = escapeHTML(pending ? t('adding', 'Agregando…') : variant.available ? t('add', 'Agregar al carrito') : t('sold_out', 'Agotado')) + ' <span aria-hidden="true"><svg class="td-arrow-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 18 18 6M6 6h12v12"/></svg></span>';
      });
    }
    function selectVariant(id, persist = true) {
      const found = variants.find(item => item.id === String(id));
      if (!found || pending) return;
      variant = found;
      current = 0;
      const price = $('#product-price');
      if (price) price.innerHTML = escapeHTML(money(variant.price)) + ' <small>' + escapeHTML(product.currency || 'CLP') + '</small>';
      const saving = Math.max(0, Number(variant.compareAtPrice || 0) - Number(variant.price));
      setHidden('#product-compare', !saving);
      setHidden('#product-savings', !saving);
      if ($('#product-compare')) $('#product-compare').innerHTML = saving ? escapeHTML(t('before', 'Antes')) + ' <s>' + escapeHTML(money(variant.compareAtPrice)) + '</s>' : '';
      if ($('#product-savings')) $('#product-savings').innerHTML = saving ? escapeHTML(t('save', 'Ahorras')) + ' ' + escapeHTML(money(saving)) + ' <span>(' + Math.round(saving / variant.compareAtPrice * 100) + '%)</span>' : '';
      text('#product-stock', variant.available ? t('available', 'Disponible') : t('sold_out', 'Agotado'));
      text('#dimension-title', stepLabel());
      text('#dimension-values', t('dimension_format', '{{height}} alto × {{length}} largo × {{width}} ancho cm', { height: variant.dimensions.heightCm, length: variant.dimensions.lengthCm, width: variant.dimensions.widthCm }));
      if (persist) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
      clearError();
      syncPurchase();
      renderGallery();
    }
    async function existingDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && !customElements.get('cart-drawer')) await Promise.race([customElements.whenDefined('cart-drawer'), new Promise(resolve => setTimeout(resolve, 1500))]);
      return drawer && typeof drawer.open === 'function' ? drawer : null;
    }
    async function refreshSections(sectionIds) {
      const url = new URL(location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url.pathname + url.search, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const sections = await response.json();
      return sectionIds.every(id => typeof sections?.[id] === 'string' && sections[id]) ? sections : null;
    }
    async function reconcileCartError(drawer, sectionIds, id) {
      if (!drawer || !sectionIds.length) return;
      try {
        const response = await fetch(localeRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        if (!cart.items?.some(item => String(item.variant_id || item.id) === id)) return;
        const sections = await refreshSections(sectionIds);
        if (sections) drawer.renderContents({ ...cart, sections });
      } catch {}
    }
    async function addToCart(trigger) {
      if (pending || !variant.available) return;
      quantity = safeQuantity($('#quantity')?.value || quantity);
      const selectedId = variant.id;
      const selectedQuantity = quantity;
      const body = new FormData(form);
      body.set('id', selectedId);
      body.set('quantity', String(selectedQuantity));
      body.delete('variant');
      pending = true;
      clearError();
      syncPurchase();
      let added = false;
      let hasError = false;
      let drawer;
      let sectionIds = [];
      event('start');
      try {
        drawer = await existingDrawer();
        sectionIds = [...new Set((drawer?.getSectionsToRender?.() || []).map(section => section.id))].filter(Boolean).slice(0, 5);
        drawer?.setActiveElement?.(trigger || document.activeElement);
        if (sectionIds.length) { body.set('sections', sectionIds.join(',')); body.set('sections_url', location.pathname); }
        const response = await fetch(window.routes?.cart_add_url || localeRoot() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error(typeof result.description === 'string' ? result.description : t('add_error', 'No pudimos agregar esta selección. Revisa su disponibilidad.'));
          error.cartReview = true;
          if (response.status === 422 || result.status === 422 || response.status === 400) await reconcileCartError(drawer, sectionIds, selectedId);
          throw error;
        }
        added = true;
        event('success', { response: result });
        if (!drawer || typeof drawer.renderContents !== 'function' || !sectionIds.length) { location.assign(cartURL()); return; }
        if (sectionIds.some(id => !result.sections?.[id])) result.sections = await refreshSections(sectionIds);
        if (!result.sections || sectionIds.some(id => !result.sections[id])) { location.assign(cartURL()); return; }
        drawer.renderContents(result);
      } catch (error) {
        if (added) { location.assign(cartURL()); return; }
        hasError = true;
        const ambiguous = error instanceof TypeError || error instanceof SyntaxError;
        const message = ambiguous ? t('cart_uncertain', 'No pudimos confirmar la operación. Revisa tu carrito antes de volver a agregar.') : error.message || t('generic_error', 'No pudimos agregar la rampa. Revisa tu carrito antes de intentarlo otra vez.');
        event('error', { error, message });
        showError(message, ambiguous || error.cartReview);
      } finally {
        pending = false;
        if (!destroyed) syncPurchase();
        event('end', { hasError });
      }
    }
    function zoom(opener) {
      const item = media();
      if (suppressZoom || !item || item.type === 'video') return;
      const image = $('#zoom-image');
      if (image) { image.src = mediaSource(item); image.alt = item.alt || item.caption; }
      text('#zoom-caption', item.caption + ' · ' + stepLabel());
      openDialog('#zoom-dialog', opener);
    }
    function navigate(delta) { if (!variant.gallery?.length) return; current = (current + delta + variant.gallery.length) % variant.gallery.length; renderGallery(); }
    listen(root, 'click', click => {
      const target = click.target.closest('button,a');
      if (!target || !root.contains(target)) return;
      if (target.matches('[data-close]')) { click.preventDefault(); const dialog = target.closest('dialog'); if (typeof dialog?.close === 'function') dialog.close(); else dialog?.removeAttribute('open'); }
      if (target.matches('[data-policy]')) { click.preventDefault(); openDialog('#condiciones-prueba', target); }
      if (target.matches('[data-guide]')) { click.preventDefault(); openDialog('#guide-dialog', target); }
      if (target.matches('[data-shipping]')) { click.preventDefault(); openDialog('#shipping-dialog', target); }
      if (target.matches('[data-shipping-details]')) {
        click.preventDefault();
        const details = $('#shipping-details');
        if (details) { details.open = true; details.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'center' }); }
        $('#shipping-region')?.focus({ preventScroll: true });
      }
      if (target.matches('[data-select-variant]')) {
        click.preventDefault();
        if (pending) return;
        selectVariant(target.dataset.selectVariant);
        target.closest('dialog')?.close();
        $('#comprar')?.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
        $$('[name="id"][type="radio"]').find(input => input.value === variant.id)?.focus({ preventScroll: true });
      }
      if (target.matches('[data-media]')) { const index = Number(target.dataset.media); if (Number.isInteger(index) && variant.gallery?.[index]) { current = index; renderGallery(); } }
    });
    $$('[name="id"][type="radio"]').forEach(input => listen(input, 'change', () => selectVariant(input.value)));
    listen($('#quantity'), 'change', () => { quantity = safeQuantity($('#quantity').value); syncPurchase(); });
    listen($('#quantity-minus'), 'click', () => { quantity--; syncPurchase(); });
    listen($('#quantity-plus'), 'click', () => { quantity++; syncPurchase(); });
    listen($('#shipping-region'), 'change', change => {
      const option = change.currentTarget.selectedOptions[0];
      text('#shipping-estimate', option?.dataset.estimate || defaultShippingEstimate);
    });
    listen(form, 'submit', submit => { submit.preventDefault(); addToCart(submit.submitter || document.activeElement); });
    listen($('#sticky-add'), 'click', click => {
      if (click.currentTarget.form === form) return;
      click.preventDefault();
      if (typeof form.requestSubmit === 'function') form.requestSubmit($('#add-to-cart'));
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
    listen($('#gallery-prev'), 'click', () => navigate(-1));
    listen($('#gallery-next'), 'click', () => navigate(1));
    listen($('#zoom-button'), 'click', click => zoom(click.currentTarget));
    listen($('#image-open'), 'click', click => zoom(click.currentTarget));
    listen($('#gallery-stage'), 'keydown', key => {
      if (media()?.type === 'video') return;
      if (key.key === 'ArrowRight' || key.key === 'ArrowLeft') { key.preventDefault(); navigate(key.key === 'ArrowRight' ? 1 : -1); }
    });
    listen($('#gallery-stage'), 'pointerdown', pointer => { if (pointer.pointerType === 'touch' && media()?.type !== 'video') pointerStart = { x: pointer.clientX, y: pointer.clientY }; });
    listen($('#gallery-stage'), 'pointercancel', () => { pointerStart = null; });
    listen($('#gallery-stage'), 'pointerup', pointer => {
      if (!pointerStart) return;
      const dx = pointer.clientX - pointerStart.x;
      const dy = pointer.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) > 45 && Math.abs(dy) < 70) { suppressZoom = true; navigate(dx < 0 ? 1 : -1); setTimeout(() => { suppressZoom = false; }, 300); }
    });
    listen($('#video-play'), 'click', async () => {
      if (!galleryVideo || blocked()) return;
      const token = ++galleryToken;
      const item = media();
      if (item?.type !== 'video') return;
      pauseAll(galleryVideo);
      if (!galleryVideo.getAttribute('src')) { galleryVideo.src = videoSource(item); galleryVideo.load(); }
      try {
        await galleryVideo.play();
        if (token !== galleryToken || blocked()) { galleryVideo.pause(); return; }
        setHidden('#video-play', true);
        $('#gallery-stage')?.classList.add('playing');
      } catch (error) {
        if (token !== galleryToken || error?.name === 'AbortError') return;
        const fallback = $('#video-fallback');
        if (fallback) { fallback.href = videoSource(item); fallback.hidden = false; }
        setHidden('#video-play', true);
      }
    });
    listen(galleryVideo, 'pause', () => { $('#gallery-stage')?.classList.remove('playing'); setHidden('#video-play', false); });
    listen(galleryVideo, 'ended', () => { $('#gallery-stage')?.classList.remove('playing'); setHidden('#video-play', false); });
    function loadUGC(video) {
      if (video.getAttribute('src') || !video.dataset.src) return;
      if (video.dataset.poster) video.poster = video.dataset.poster;
      video.src = video.dataset.src;
      video.preload = 'metadata';
      video.load();
    }
    function ugcError(video) { const mediaElement = video.closest('.ugc-media'); const error = mediaElement?.querySelector('.ugc-error'); if (error) error.hidden = false; const play = mediaElement?.querySelector('.ugc-play'); if (play) play.hidden = true; }
    async function playUGC(video, manual = false) {
      if (blocked()) return;
      const token = ++playbackToken;
      attempted.add(video);
      pauseAll(video);
      loadUGC(video);
      const mediaElement = video.closest('.ugc-media');
      const error = mediaElement.querySelector('.ugc-error');
      if (error) error.hidden = true;
      try {
        await video.play();
        if (token !== playbackToken || blocked() || (!manual && (ratios.get(video) || 0) < .65)) video.pause();
      } catch (failure) {
        if (token !== playbackToken || failure?.name === 'AbortError') return;
        if (video.error) ugcError(video);
        else { const play = mediaElement.querySelector('.ugc-play'); if (play) play.hidden = false; }
      }
    }
    function maybeAutoplay() {
      if (!ugcVideos.length || blocked() || reducedMotion.matches || saveData() || allVideos.some(video => !video.paused && !video.ended)) return;
      const best = ugcVideos.reduce((winner, video) => (ratios.get(video) || 0) > (ratios.get(winner) || 0) ? video : winner, ugcVideos[0]);
      if ((ratios.get(best) || 0) >= .65 && !attempted.has(best)) playUGC(best);
    }
    allVideos.forEach(video => listen(video, 'play', () => { if (blocked()) { video.pause(); return; } pauseAll(video); }));
    ugcVideos.forEach(video => {
      const mediaElement = video.closest('.ugc-media');
      const button = mediaElement?.querySelector('.ugc-play');
      video.muted = true;
      listen(button, 'click', () => playUGC(video, true));
      listen(mediaElement?.querySelector('.ugc-retry'), 'click', () => { video.removeAttribute('src'); playUGC(video, true); });
      listen(video, 'play', () => { attempted.add(video); mediaElement.dataset.started = 'true'; if (button) button.hidden = true; const error = mediaElement.querySelector('.ugc-error'); if (error) error.hidden = true; });
      listen(video, 'pause', () => { if (button && !video.error) button.hidden = false; });
      listen(video, 'error', () => ugcError(video));
    });
    if ('IntersectionObserver' in window) {
      const nearby = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting && !saveData() && !reducedMotion.matches) { loadUGC(entry.target); nearby.unobserve(entry.target); } }), { rootMargin: '180px 0px', threshold: .01 });
      const visibility = new IntersectionObserver(entries => { entries.forEach(entry => { const ratio = entry.isIntersecting ? entry.intersectionRatio : 0; ratios.set(entry.target, ratio); if (ratio < .2) entry.target.pause(); }); maybeAutoplay(); }, { threshold: [0, .2, .65, .9, 1] });
      observers.push(nearby, visibility);
      ugcVideos.forEach(video => { nearby.observe(video); visibility.observe(video); });
      if ($('#gallery-stage')) observe(new IntersectionObserver(entries => { entries.forEach(entry => { if (!entry.isIntersecting) { galleryToken++; galleryVideo?.pause(); } }); }, { threshold: .1 }), $('#gallery-stage'));
      if ($('#add-to-cart')) observe(new IntersectionObserver(updateSticky, { threshold: 0 }), $('#add-to-cart'));
    }
    const overlayChanged = () => { updateSticky(); if (hasOverlay()) { playbackToken++; galleryToken++; pauseAll(); } };
    $$('dialog').forEach(dialog => observe(new MutationObserver(overlayChanged), dialog, { attributes: true, attributeFilter: ['open'] }));
    document.querySelectorAll('cart-drawer,cart-notification,details.menu-drawer-container').forEach(drawer => observe(new MutationObserver(overlayChanged), drawer, { attributes: true, attributeFilter: ['class', 'open'] }));
    listen(document, 'visibilitychange', () => { if (document.hidden) { playbackToken++; galleryToken++; pauseAll(); } });
    listen(window, 'scroll', scheduleSticky, { passive: true });
    listen(window, 'resize', scheduleSticky, { passive: true });
    listen(window, 'popstate', () => { const id = new URLSearchParams(location.search).get('variant'); selectVariant(id || defaultVariant.id, false); });
    const ugcSection = $('.ugc-section');
    const track = ugcSection?.querySelector('.ugc-track');
    function updateNavigation() {
      if (!track) return;
      const previous = ugcSection.querySelector('.ugc-prev');
      const next = ugcSection.querySelector('.ugc-next');
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }
    function moveUGC(direction) {
      const card = track?.querySelector('.ugc-card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
    listen(ugcSection?.querySelector('.ugc-prev'), 'click', () => moveUGC(-1));
    listen(ugcSection?.querySelector('.ugc-next'), 'click', () => moveUGC(1));
    listen(track, 'keydown', key => { if (key.target === track && ['ArrowLeft', 'ArrowRight'].includes(key.key)) { key.preventDefault(); moveUGC(key.key === 'ArrowLeft' ? -1 : 1); } });
    listen(track, 'scroll', updateNavigation, { passive: true });
    listen(window, 'resize', updateNavigation, { passive: true });
    const destroy = () => { destroyed = true; playbackToken++; galleryToken++; pauseAll(); abort.abort(); observers.forEach(observer => observer.disconnect()); if (stickyFrame) cancelAnimationFrame(stickyFrame); instances.delete(root); };
    instances.set(root, { destroy });
    selectVariant(variant.id, false);
    updateSticky();
    updateNavigation();
  }
  const init = () => document.querySelectorAll('#td-rampa-editorial').forEach(initialize);
  window.TDRampaEditorial = Object.freeze({ init });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => { const root = event.target.matches?.('#td-rampa-editorial') ? event.target : event.target.querySelector?.('#td-rampa-editorial'); if (root) instances.get(root)?.destroy(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
